// NGRX
import * as _ from 'lodash';
import * as utils from '../util';
import { GrantorInfo, Guest } from '../models';
import { OlciActionTypes } from '../actions';
import { constants } from '../constants/constants';
import { GuestReservationId } from '../models/arrivalAppointment';
import { ITravelQuestionnaire } from '../models/travel-questionnaire.payload';
import { IAuthorizedConsentsGrantee, IAuthorizedConsentsGrantor, IContractDocuments } from '../models/authorized-consents.payload';

export interface State {
	loaded: boolean;
	error: any;
	emailError: any;
	loadingQuestionnaire: boolean;
	travelQuestionnaire: ITravelQuestionnaire[];
	grantorInfo: GrantorInfo | null;
	grantees: IAuthorizedConsentsGrantee[];
	contractsContent: { [contract: string]: string } | null;

	granteeConsentsInfo: any[];
	granteeConsentsList: Guest[];
}

export const initialState: State = {
	loaded: true,
	error: null,
	emailError: null,
	loadingQuestionnaire: false,
	travelQuestionnaire: [],
	grantorInfo: null,
	grantees: [],
	contractsContent: null,

	granteeConsentsInfo: [],
	granteeConsentsList: [],
};

export function reducer(
	state: State = initialState,
	action: any
): State {
	switch (action.type) {
    case OlciActionTypes.LOAD_CRUISE_CONTRACT:
			return {
        ...state,
				loaded: false,
				error: null
			};

		case OlciActionTypes.LOAD_CRUISE_CONTRACT_SUCCESS:
			return loadCruiseContractSuccessReducer(state, action.payload);

		case OlciActionTypes.UPDATE_CRUISE_CONTRACT_DETAILS: {
			const grantor = action.payload.grantor;
			const grantees = action.payload.grantees;
			return  {
        ...state,
				loaded: false,
				grantorInfo: grantor,
				granteeConsentsList: grantees,
				error: null
			};
		}

		case OlciActionTypes.SAVE_AUTHORIZATION_CONSENTS_SUCCESS:
      return saveAuthorizationConsentSuccessReducer(state, action.payload);

		case OlciActionTypes.DELETE_BIOMETRICS_AUTHORIZATIONS_SUCCESS:
      return deleteBiometricsAuthorizationsSuccessReducer(state, action.payload);

		case OlciActionTypes.LOAD_TRAVEL_QUESTIONNAIRE:
			return {
        ...state,
        loadingQuestionnaire: true, 
        error: null 
      };

		case OlciActionTypes.LOAD_TRAVEL_QUESTIONNAIRE_SUCCESS:
      return loadTravelQuestionnaireSuccessReducer(state, action.payload);

		case OlciActionTypes.CREATE_UPDATE_TRAVEL_QUESTIONNAIRE_SUCCESS:
      return createUpdateTravelQuestionnaireSuccessReducer(state, action.payload);
		default:
			return state;
	}
}

export function mapTravelQuestionnaireResponse(response: any, currentState: any): ITravelQuestionnaire[] {
	let travelQuestionnaire: ITravelQuestionnaire[] = [];
	const filteredCurrentState: any[] = [];
	const questionnaires = _.get(response, 'payload.questionnaire', []);
	if (!questionnaires.length) {
		return travelQuestionnaire;
	}
	else {
		const questionnaireMap = questionnaires.map((q: any) => {
			return {
				guestId: q.guestReservationId?.guestId ?? null,
				bookingId: q.guestReservationId?.bookingId ?? null,
				hq1: q.types?.length ? getQuestionValue(constants.CONSENTS_DOCUMENTS_TYPE.HQ1, q.types) : '',
			}
		});
		if (currentState.length) {
			currentState.forEach((cs: any) => {
				const exist = questionnaireMap.find((q: any) => q.guestId === cs.guestId);
				if (!exist) {
					filteredCurrentState.push(cs);
				}
			})
		}

		travelQuestionnaire = [...filteredCurrentState, ...questionnaireMap
		];
	}

	return travelQuestionnaire;
}

export function getQuestionValue(questionType: any, typesArray: any): string {
	const type = typesArray.find((v: any) => v.value === questionType);
	return type?.flag ? utils.capitalizeNameAllWords(type.flag) : '';
}

export function getIndexOfGrantee(granteeArray: any, guestId: any): number {
	let index = -1;
	for (let i = 0; i < granteeArray.length; i++) {
		if (granteeArray[i].guestReservationId.guestId === guestId) {
			index = i;
			break;
		}
	}
	return index;
}

function loadCruiseContractSuccessReducer(state: State, actionPayload: any): State {
  if (actionPayload.status !== constants.HTTP_STATUS_CODE.SUCCESS) {
    return {
      ...state,
      error: actionPayload.errors,
      loaded: true
    };
  }
  const cruiseContractContent = actionPayload;
  return {
    ...state,
    loaded: true,
    grantorInfo: cruiseContractContent.grantorInfo,
    grantees: cruiseContractContent.payload.grantees,
    contractsContent: cruiseContractContent.allContractsContent,
    error: null
  };
}

function saveAuthorizationConsentSuccessReducer(state: State, actionPayload: any): State {
  if (actionPayload.status !== constants.HTTP_STATUS_CODE.SUCCESS) {
    return {
      ...state,
      error: actionPayload.errors
    };
  }
  const response = actionPayload;
  const contractsTypes: string[] = response.payload.contractTypes;
  const successfulGrantees: GuestReservationId[] = response.payload.successfulGrantees;

  const newGranteesAfterSave = state.grantees ? [...state.grantees] : [];
  completeGranteesInformation(state,successfulGrantees,contractsTypes,newGranteesAfterSave);

  return {
    ...state,
    grantees: newGranteesAfterSave,
    loaded: true,
    error: null
  };
}

function completeGranteesInformation(state: State, successfulGrantees: GuestReservationId[], contractsTypes: string[], newGranteesAfterSave: any) {
  successfulGrantees.forEach(sg => {
    const grantee = state.grantees.find(g => g.guestReservationId.guestId === sg.guestId);
    if (grantee) {
      const index = getIndexOfGrantee(newGranteesAfterSave, sg.guestId);
      if (index !== -1) {
        newGranteesAfterSave.splice(index, 1);
      }

      let grantor: IAuthorizedConsentsGrantor = grantee.grantor;
      grantor = grantor ?? getValuesToGrantorIfNotDefined(state);
      const signedDocuments: IContractDocuments[] = grantee.signedDocuments ? [...grantee.signedDocuments] : [];
      const unsignedDocuments = grantee.unsignedDocuments ? grantee.unsignedDocuments : [];
      const newUnsignedDocuments: IContractDocuments[] = reorderUnsignedAndSignedDocuments(unsignedDocuments, contractsTypes, signedDocuments);

      newGranteesAfterSave.push({
        grantor,
        guestReservationId: grantee.guestReservationId,
        signedDocuments: signedDocuments,
        unsignedDocuments: newUnsignedDocuments
      });
    }

  })

  return {
    ...state, 
    grantees: newGranteesAfterSave,
    loaded: true,
    error: null
  };
}

export const getValuesToGrantorIfNotDefined = (state: State): IAuthorizedConsentsGrantor => {
  return {
    guestReservationId: {
      guestId: state.grantorInfo?.guestId ?? null,
      bookingId: state.grantorInfo?.bookingId ?? null
    },
    firstName: state.grantorInfo?.firstName ?? null,
    lastName: state.grantorInfo?.lastName ?? null
  } as IAuthorizedConsentsGrantor;
}

export const reorderUnsignedAndSignedDocuments = (
  unsignedDocuments: IContractDocuments[], contractsTypes: string[], signedDocuments: IContractDocuments[]
): IContractDocuments[] => {
  let  newUnsignedDocuments: IContractDocuments[] = [];
  if (unsignedDocuments.length) {
    contractsTypes.forEach(contractType => {
      const contract = unsignedDocuments.find(document => document.type === contractType);
      if (contract) {
        signedDocuments.push(contract);
        if (!newUnsignedDocuments.length) {
          newUnsignedDocuments = unsignedDocuments.filter(document => document.type !== contractType);
        }
        else {
          newUnsignedDocuments = newUnsignedDocuments.filter((document: any) => document.type !== contractType);
        }
      }
    })
  }
  return newUnsignedDocuments;
}

function deleteBiometricsAuthorizationsSuccessReducer(state: State, actionPayload: any): State {
  const successfulDeletedConsents = actionPayload.authorizedConsents;
  const newGranteesAfterDelete = state.grantees ? [...state.grantees] : [];

  successfulDeletedConsents.forEach((consent: any) => {

    const granteeToUpdate: IAuthorizedConsentsGrantee | any = newGranteesAfterDelete.find(grantee => {
      const { bookingId, guestId } = grantee.guestReservationId;
      return consent.guestId === guestId && consent.bookingId === bookingId;
    });

    if (granteeToUpdate) {
      const deletedConsent = granteeToUpdate.signedDocuments.find((signedConsent: any) => consent.type === signedConsent.type);
      if (deletedConsent) {
        granteeToUpdate.signedDocuments = granteeToUpdate.signedDocuments.filter((signedConsent: any) => consent.type !== signedConsent.type);
        granteeToUpdate.unsignedDocuments.push(deletedConsent);
      }
    }
  });

  return {
    ...state,
    grantees: newGranteesAfterDelete,
    loaded: true,
    error: null
  };
}

function loadTravelQuestionnaireSuccessReducer(state: State, actionPayload: any): State {
  const errors = _.get(actionPayload, 'errors', []);
  if (actionPayload.status !== constants.HTTP_STATUS_CODE.SUCCESS) {
    return {
      ...state,
      loadingQuestionnaire: false,
      error: errors
    };
  }
  return {
    ...state,
    loadingQuestionnaire: false,
    error: null,
    travelQuestionnaire: mapTravelQuestionnaireResponse(actionPayload, state.travelQuestionnaire)
  };
}

function createUpdateTravelQuestionnaireSuccessReducer(state: State, actionPayload: any): State {
  if (actionPayload.status !== constants.HTTP_STATUS_CODE.SUCCESS) {
    return {
      ...state,
      error: _.get(actionPayload, 'errors', [])
    };
  }
  return {
    ...state,
    error: null,
    travelQuestionnaire: mapTravelQuestionnaireResponse(actionPayload, state.travelQuestionnaire)
  };
}

export const getContractsContent = (state: State) => state.contractsContent;
export const getGranteeConsentsInfo = (state: State) => state.granteeConsentsInfo;
export const getGrantees = (state: State) => state.grantees;
export const getgranteeConsentsList = (state: State) => state.granteeConsentsList;
export const getGrantorInfo = (state: State) => state.grantorInfo;
export const getErrors = (state: State) => state.error;
export const getTravelQuestionnaire = (state: State) => state.travelQuestionnaire;
